/**
 * Theme configuration - Apple-style Design System
 * Supports light, dark, and system themes
 */
import { createTheme, ThemeOptions } from '@mui/material/styles';

// Global Table Typography Constants
export const TABLE_TYPOGRAPHY = {
  header: {
    fontSize: '0.8125rem',
    fontWeight: {
      normal: 500,
      sorted: 600,
    },
  },
  data: {
    fontSize: '0.875rem',
    fontWeight: {
      normal: 400,
      sorted: 600,
    },
  },
} as const;

// Modern Color Gradients (2026 Design System)
export const COLOR_GRADIENTS = {
  orangePrimary: 'linear-gradient(135deg, #FF6200 0%, #F97316 50%, #FDA766 100%)',
  orangeButton: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
  hero: 'linear-gradient(-45deg, #FF6200, #F97316, #FDA766, #FCBF49)',
  tealAccent: 'linear-gradient(135deg, #06B6D4 0%, #38BDF8 100%)',
  purpleAccent: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
} as const;

// DataFactz Orange theme with 2026 modern enhancements
const lightTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#F97316',      // DataFactz Orange (Keep as main CTA)
      light: '#FDA766',     // Light Orange for highlights
      dark: '#E65C00',      // Dark Orange for emphasis
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#06B6D4',      // Teal/Cyan (pairs beautifully with orange)
      light: '#38BDF8',
      dark: '#0891B2',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    info: {
      main: '#1E40AF',      // Deep Blue for info/links
      light: '#3B82F6',
      dark: '#1E3A8A',
    },
    background: {
      default: '#F9FAFB',   // Off-white modern background
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1D1D1F',
      secondary: '#6B7280',
      disabled: '#C7C7CC',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
  },
  typography: {
    fontFamily: [
      '"SF Pro Display"',
      '"SF Pro Text"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.25rem', // Increased for better hierarchy
      fontWeight: 700, // Bolder for emphasis
      lineHeight: 1.2,
      letterSpacing: '-0.03em', // Increased negative spacing for large text
    },
    h2: {
      fontSize: '1.75rem', // Increased spacing from h3
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.25rem', // Increased for better visual weight
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '1.125rem', // Slightly larger
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontSize: '1rem', // Increased from 0.875rem
      fontWeight: 500,
      lineHeight: 1.43,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '0.875rem', // Increased from 0.75rem
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0em',
    },
    body1: {
      fontSize: '0.9375rem',
      fontWeight: 400,
      lineHeight: 1.6, // Increased for better readability
      letterSpacing: '-0.01em',
    },
    body2: {
      fontSize: '0.875rem', // Slightly larger
      fontWeight: 400,
      lineHeight: 1.5, // Better readability
      letterSpacing: '-0.005em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '-0.01em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5, // Increased for readability
      letterSpacing: '0.01em',
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600, // Bolder for emphasis
      lineHeight: 1.5,
      letterSpacing: '0.08em', // Increased for uppercase text
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 4,
  // Multi-layer shadows with subtle orange tint for brand consistency
  shadows: [
    'none',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 1px 2px rgba(249, 115, 22, 0.04)',
    '0 2px 4px rgba(249, 115, 22, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
    '0 4px 8px rgba(249, 115, 22, 0.06), 0 2px 4px rgba(0, 0, 0, 0.03)',
    '0 8px 16px rgba(249, 115, 22, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          // Global focus-visible styles for accessibility (WCAG 2.4.7)
          '& *:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '2px',
          },
          '& a:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '2px',
            textDecoration: 'underline',
          },
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.12) transparent',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: 'none',
          },
          // Enhanced focus indicator for accessibility (WCAG 2.4.7)
          '&:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '2px',
            boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.2)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E65C00 0%, #F97316 100%)',
            boxShadow: '0 4px 14px rgba(249, 115, 22, 0.4)',
          },
          '&:focus-visible': {
            outline: '3px solid #FFFFFF',
            outlineOffset: '2px',
            boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(249, 115, 22, 0.2)',
          '&:hover': {
            borderColor: '#F97316',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
            transform: 'translateY(-1px)',
          },
          '&:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '2px',
            borderColor: '#F97316',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          // Premium glassmorphism - 2026 Framer/Apple style with lower opacity
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.25)',
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // Solid background for tables - no transparency for better readability
          background: '#FFFFFF',
          border: '1px solid rgba(249, 115, 22, 0.15)',
        },
        rounded: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(249, 115, 22, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(249, 115, 22, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.08)',
              transition: 'all 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.16)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F97316',
              borderWidth: 2,
            },
            // Enhanced focus indicator for accessibility
            '&.Mui-focused': {
              boxShadow: '0 0 0 4px rgba(249, 115, 22, 0.15)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'inset 0 -1px 0 rgba(249, 115, 22, 0.1)',
          borderBottom: '1px solid rgba(249, 115, 22, 0.08)',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.85) 100%)',
          backdropFilter: 'saturate(200%) blur(25px)',
          color: '#1D1D1F',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.8125rem',
          fontWeight: 500,
          transition: 'all 0.15s ease',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          // Enhanced focus indicator for accessibility
          '&:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '2px',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#FAFAFA',
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0',
            color: '#64748B',
            textTransform: 'none',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,
          fontVariant: 'normal',
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
          padding: '14px 16px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(249, 115, 22, 0.12)',
            },
          },
          // Enhanced focus indicator for accessibility
          '&:focus-visible': {
            outline: '3px solid #F97316',
            outlineOffset: '-3px',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)',
          backdropFilter: 'blur(30px) saturate(180%)',
          boxShadow: '0 24px 48px rgba(249, 115, 22, 0.15), 0 0 0 1px rgba(249, 115, 22, 0.05)',
          border: '1px solid rgba(249, 115, 22, 0.08)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          padding: '8px 12px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(29, 29, 31, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 8,
          fontSize: '0.8125rem',
          padding: '8px 12px',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: '#34C759',
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 28,
          height: 28,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        },
        track: {
          borderRadius: 16,
          backgroundColor: '#E5E5EA',
          opacity: 1,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          minHeight: 48,
        },
      },
    },
  },
};

// Enhanced Dark Mode (2026 Modern - Rich Navy Backgrounds)
const darkTheme: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#FB923C',      // Softer Orange for dark mode
      light: '#FDBA74',
      dark: '#F97316',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#38BDF8',      // Sky Blue (lighter for dark mode)
      light: '#7DD3FC',
      dark: '#0891B2',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#F87171',
      light: '#FCA5A5',
      dark: '#EF4444',
    },
    warning: {
      main: '#FBBF24',
      light: '#FCD34D',
      dark: '#F59E0B',
    },
    success: {
      main: '#34D399',
      light: '#6EE7B7',
      dark: '#10B981',
    },
    info: {
      main: '#60A5FA',
      light: '#93C5FD',
      dark: '#3B82F6',
    },
    background: {
      default: '#0F172A',   // Navy (not pure black - 2026 best practice)
      paper: '#1E293B',     // Slate for cards/surfaces
    },
    text: {
      primary: '#F3F4F6',   // Off-white (not pure white - better contrast)
      secondary: '#9CA3AF',
      disabled: '#52525B',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: lightTheme.typography,
  shape: lightTheme.shape,
  spacing: lightTheme.spacing,
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
    '0 1px 2px rgba(0, 0, 0, 0.2)',
    '0 2px 4px rgba(0, 0, 0, 0.2)',
    '0 4px 8px rgba(0, 0, 0, 0.2)',
    '0 8px 16px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    ...lightTheme.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          // Global focus-visible styles for accessibility (WCAG 2.4.7 - Dark Mode)
          '& *:focus-visible': {
            outline: '3px solid #FB923C',
            outlineOffset: '2px',
          },
          '& a:focus-visible': {
            outline: '3px solid #FB923C',
            outlineOffset: '2px',
            textDecoration: 'underline',
          },
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            borderRadius: 4,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.12) transparent',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'inset 0 -1px 0 rgba(251, 146, 60, 0.08)',
          borderBottom: '1px solid rgba(251, 146, 60, 0.1)',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.85) 100%)',
          backdropFilter: 'saturate(200%) blur(25px)',
          color: '#F3F4F6',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: 'none',
          backgroundColor: '#1E293B', // Slate
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          // Premium glassmorphism - 2026 dark mode with lower opacity for more transparency
          background: 'rgba(17, 25, 40, 0.25)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'rgba(17, 25, 40, 0.4)',
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // Solid background for tables - no transparency for better readability (dark mode)
          background: '#1E293B',
          border: '1px solid rgba(251, 146, 60, 0.15)',
        },
        rounded: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#334155', // Darker slate for headers
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0',
            color: '#9CA3AF',
            textTransform: 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,
          fontVariant: 'normal',
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          padding: '14px 16px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          // Enhanced focus indicator for accessibility (dark mode)
          '&:focus-visible': {
            outline: '3px solid #FB923C',
            outlineOffset: '2px',
            backgroundColor: 'rgba(251, 146, 60, 0.15)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(249, 115, 22, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(249, 115, 22, 0.2)',
            },
          },
          // Enhanced focus indicator for accessibility (dark mode)
          '&:focus-visible': {
            outline: '3px solid #FB923C',
            outlineOffset: '-3px',
            backgroundColor: 'rgba(251, 146, 60, 0.15)',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: '#334155', // Darker slate for menus
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 6px',
          padding: '8px 12px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%)',
          backdropFilter: 'blur(30px) saturate(180%)',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(251, 146, 60, 0.1)',
          backgroundColor: '#1E293B', // Slate fallback
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(51, 65, 85, 0.95)', // Darker slate for tooltips
          backdropFilter: 'blur(10px)',
          borderRadius: 8,
          fontSize: '0.8125rem',
          padding: '8px 12px',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: '#30D158',
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 28,
          height: 28,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        },
        track: {
          borderRadius: 16,
          backgroundColor: '#39393D',
          opacity: 1,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
              transition: 'all 0.2s ease',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#F97316',
              borderWidth: 2,
            },
            // Enhanced focus indicator for accessibility (dark mode)
            '&.Mui-focused': {
              boxShadow: '0 0 0 4px rgba(251, 146, 60, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#F97316',
            },
          },
        },
      },
    },
  },
};

export const createAppTheme = (mode: 'light' | 'dark') => {
  return createTheme(mode === 'light' ? lightTheme : darkTheme);
};

export default createAppTheme;
